import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import DbArticle from "./DbArticle";
import { ArrowLeft, ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import Header from "../../components/landing/Header";
import Footer from "../../components/landing/Footer";
import Seo from "../../components/Seo";
import NewsletterSignup from "../../components/NewsletterSignup";
import { BLOG_POSTS, getPost, fmtBlogDate } from "../../content/blogPosts";
import { BlogCover } from "../../components/brand/BlogCover";

export default function BlogArticle() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <DbArticle slug={slug} />;
  return <StaticArticle post={post} />;
}

function StaticArticle({ post }) {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, [post.slug]);

  const url = `https://www.360immoinvest.com/blog/${post.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: post.cover,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: "fr-FR",
    mainEntityOfPage: url,
    author: { "@type": "Organization", name: "360immoinvest" },
    publisher: {
      "@type": "Organization",
      name: "360immoinvest",
      logo: { "@type": "ImageObject", url: "https://www.360immoinvest.com/aimmo360-logo.png" },
    },
  };

  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-article-page">
      <Seo title={`${post.title} | 360immoinvest`} description={post.description} path={`/blog/${post.slug}`} jsonLd={jsonLd} />
      <Header />
      <main>
        <article className="pt-32">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-brand-green/70 hover:text-brand-greenDeep transition-colors mb-8" data-testid="blog-back">
              <ArrowLeft className="w-4 h-4" /> Tous les articles
            </Link>
            <div className="flex items-center gap-3 text-xs font-semibold text-brand-limeDark uppercase tracking-wide">
              <span>{post.category}</span>
              <span className="text-brand-green/40 inline-flex items-center gap-1 normal-case tracking-normal font-medium"><Clock className="w-3.5 h-3.5" /> {post.readingTime}</span>
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold text-brand-greenDeep leading-tight mt-3">{post.title}</h1>
            <p className="mt-3 text-sm text-brand-green/45">Publié le {fmtBlogDate(post.date)}</p>
          </div>

          <div className="max-w-4xl mx-auto px-5 lg:px-8 mt-8">
            <div className="rounded-[10px] overflow-hidden border border-brand-soft h-[300px] lg:h-[420px]">
              <BlogCover post={post} index={BLOG_POSTS.indexOf(post)} size="lg" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-10">
            {post.intro && <p className="text-lg text-brand-green/80 leading-relaxed font-medium">{post.intro}</p>}
            {post.sections.map((s, i) => (
              <section key={i} className="mt-9">
                <h2 className="font-display text-2xl font-semibold text-brand-greenDeep mb-3">{s.title}</h2>
                {(s.paragraphs || []).map((p, j) => (
                  <p key={j} className="text-brand-green/80 leading-relaxed text-[15px] mb-3">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="list-disc pl-5 space-y-1.5 text-brand-green/80 text-[15px]">
                    {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {/* Newsletter */}
          <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-12">
            <NewsletterSignup source="blog-article" />
          </div>

          {/* CTA */}
          <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-12">
            <div className="rounded-3xl bg-brand-greenDeep text-white p-8 text-center">
              <h2 className="font-display text-2xl font-semibold">{post.cta?.title || "Gérez vos locations sans la paperasse"}</h2>
              <p className="mt-2 text-white/75 text-[15px]">{post.cta?.text || "Quittances, loyers, révision IRL et revenus fonciers au même endroit. Gratuit pour 1 bien."}</p>
              <button onClick={() => navigate("/connexion")} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold" data-testid="blog-cta">
                {post.cta?.button || "Créer mon compte gratuit"} <ArrowUpRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Autres articles */}
          <div className="max-w-5xl mx-auto px-5 lg:px-8 mt-16 mb-4">
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep mb-5">À lire aussi</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {others.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl border border-brand-soft overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`blog-related-${p.slug}`}>
                  <div className="h-32 overflow-hidden">
                    <BlogCover post={p} index={BLOG_POSTS.indexOf(p)} size="sm" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-brand-greenDeep text-sm leading-snug">{p.title}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-brand-greenDeep font-semibold">Lire <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </article>
        <div className="h-16" />
      </main>
      <Footer />
    </div>
  );
}
